import { useEffect, useMemo, useState } from "react";
import { Coffee, Clock, ChatCircleText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SortHeader } from "../components/SortHeader";
import { useTableSort } from "../lib/useTableSort";
import { useRoles, visibleRolesLabel } from "../lib/roles";

const CATEGORY_META = {
  cocina: { label: "Cocina", color: "bg-orange-100 text-orange-700 border-orange-300" },
  barra: { label: "Barra", color: "bg-sky-100 text-sky-700 border-sky-300" },
  ocio: { label: "Ocio (todos)", color: "bg-slate-100 text-slate-700 border-slate-300" },
};

const PERIODS = [
  { value: "day", label: "Hoy" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

function fmtDur(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0 && m > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
  } catch { return ""; }
}

export default function AdminTasksReports() {
  const [period, setPeriod] = useState("day");
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [refDate, setRefDate] = useState(todayIso());
  const [history, setHistory] = useState({ rows: [], total_hours: 0, loading: true });
  const [view, setView] = useState("list"); // "list" | "matrix"
  const [roles] = useRoles();
  const listSort = useTableSort(history.rows || [], "start_time", "asc");

  const load = async (p = period, d = refDate) => {
    setHistory((h) => ({ ...h, loading: true }));
    try {
      const { data } = await api.get("/reports/breaks-summary", {
        params: { reason: "all", period: p, date: d },
      });
      setHistory({ rows: data.rows || [], total_hours: data.total_hours || 0, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setHistory({ rows: [], total_hours: 0, loading: false });
    }
  };

  useEffect(() => { load(period, refDate); /* eslint-disable-next-line */ }, [period, refDate]);

  // Group rows for list view: by day (desc). Mark consecutive same-reason same-user rows.
  const grouped = useMemo(() => {
    const dayOf = (r) => new Date(r.start_time).toISOString().slice(0, 10);
    const order = new Map(listSort.sortedRows.map((r, i) => [r.id, i]));
    const sorted = [...(history.rows || [])].sort((a, b) => {
      const da = dayOf(a);
      const db = dayOf(b);
      if (db !== da) return db.localeCompare(da);
      return order.get(a.id) - order.get(b.id);
    });
    const byDay = new Map();
    for (const r of sorted) {
      const day = new Date(r.start_time).toISOString().slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(r);
    }
    const out = [];
    for (const [day, rows] of byDay.entries()) {
      const marked = rows.map((row, i) => {
        const prev = rows[i - 1];
        const isRepeat = !!prev && prev.user_id === row.user_id && prev.reason === row.reason;
        return { ...row, _consecutive: isRepeat };
      });
      out.push({ day, rows: marked });
    }
    return out;
  }, [history.rows, listSort.sortedRows]);

  // Matrix: rows = reason, columns = day, cells = per-worker breakdown + totals.
  const matrix = useMemo(() => {
    const rows = history.rows || [];
    const dayKeys = Array.from(new Set(rows.map((r) => new Date(r.start_time).toISOString().slice(0, 10)))).sort();
    const reasonKeys = Array.from(new Set(rows.map((r) => (r.reason || "—").trim()))).sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
    const cells = new Map();
    for (const r of rows) {
      const day = new Date(r.start_time).toISOString().slice(0, 10);
      const reason = (r.reason || "—").trim();
      if (!cells.has(reason)) cells.set(reason, new Map());
      const dayMap = cells.get(reason);
      if (!dayMap.has(day)) dayMap.set(day, new Map());
      const userMap = dayMap.get(day);
      const key = r.user_name || "?";
      const prev = userMap.get(key) || { hours: 0, family_color: r.family_color || "#94a3b8" };
      userMap.set(key, { hours: prev.hours + (r.duration_hours || 0), family_color: r.family_color || prev.family_color });
    }
    // Per-column totals (sum across all reasons for each day)
    const colTotals = Object.fromEntries(dayKeys.map((d) => [d, 0]));
    for (const [, dayMap] of cells.entries()) {
      for (const [d, userMap] of dayMap.entries()) {
        for (const [, v] of userMap.entries()) colTotals[d] += v.hours;
      }
    }
    const grandTotal = Object.values(colTotals).reduce((s, h) => s + h, 0);
    const reasonRows = reasonKeys.map((reason) => {
      const row = { reason, total: 0 };
      for (const [d, userMap] of (cells.get(reason) || new Map()).entries()) {
        let h = 0;
        for (const [, v] of userMap.entries()) h += v.hours;
        row[d] = h;
        row.total += h;
      }
      return row;
    });
    return { dayKeys, reasonKeys, reasonRows, cells, colTotals, grandTotal };
  }, [history.rows]);
  const matrixSort = useTableSort(matrix.reasonRows, "reason", "asc");

  return (
    <div className="max-w-6xl space-y-6" data-testid="admin-tasks-reports-page">
      <div>
        <div className="label-cap">tareas</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Informes tareas</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2">
          Historial de tareas realizadas. Alterna entre lista y matriz para detectar patrones por motivo y por día.
        </p>
      </div>

      <div className="card-flat p-6 rounded-2xl">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Coffee size={22} weight="duotone" className="text-[var(--primary)]" />
            <div>
              <div className="label-cap mb-0.5">historial</div>
              <h2 className="font-display text-xl font-bold tracking-tight">Tareas realizadas</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-xl border border-[var(--border)] bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setView("list")}
                data-testid="tasks-view-list"
                className={`px-3 py-2 text-sm font-display font-semibold ${
                  view === "list" ? "bg-[var(--primary)] text-white" : "text-[var(--text-secondary)] hover:bg-[var(--bg)]"
                }`}
              >
                Lista
              </button>
              <button
                type="button"
                onClick={() => setView("matrix")}
                data-testid="tasks-view-matrix"
                className={`px-3 py-2 text-sm font-display font-semibold ${
                  view === "matrix" ? "bg-[var(--primary)] text-white" : "text-[var(--text-secondary)] hover:bg-[var(--bg)]"
                }`}
              >
                Matriz
              </button>
            </div>
            <div className="inline-flex rounded-xl border border-[var(--border)] bg-white overflow-hidden">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setPeriod(p.value)}
                  data-testid={`tasks-period-${p.value}`}
                  className={`px-3 py-2 text-sm font-display font-semibold ${
                    period === p.value ? "bg-[var(--primary)] text-white" : "text-[var(--text-secondary)] hover:bg-[var(--bg)]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <input
              type="date"
              value={refDate}
              onChange={(e) => setRefDate(e.target.value)}
              data-testid="tasks-ref-date"
              className="px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-sm font-mono"
            />
          </div>
        </div>

        <div className="text-sm text-[var(--text-secondary)] mb-3" data-testid="tasks-total">
          Total: <span className="font-mono font-semibold text-[var(--text-primary)]">{fmtDur(history.total_hours || 0)}</span>
          {" · "}
          {history.rows.length} tarea(s)
        </div>

        {history.loading ? (
          <div className="text-sm text-[var(--text-secondary)]">Cargando…</div>
        ) : history.rows.length === 0 ? (
          <div className="text-sm text-[var(--text-secondary)] italic">No hay tareas en este periodo.</div>
        ) : view === "list" ? (
          <div className="space-y-5">
            <div className="flex items-center gap-4 flex-wrap text-xs" data-testid="tasks-list-sort-bar">
              <span className="text-[var(--text-secondary)]">Ordenar:</span>
              {[["start_time", "Hora"], ["user_name", "Trabajador"], ["reason", "Tarea"], ["category", "Visibilidad"], ["duration_hours", "Duración"]].map(([k, label]) => (
                <span key={k} className="w-auto">
                  <SortHeader sortKey={k} currentKey={listSort.sortKey} currentDir={listSort.sortDir} onClick={listSort.toggleSort}>{label}</SortHeader>
                </span>
              ))}
            </div>
            {grouped.map(({ day, rows }) => (
              <div key={day} data-testid={`tasks-day-${day}`}>
                <div className="label-cap mb-2">{fmtDate(day + "T00:00:00")}</div>
                <ul className="space-y-1.5">
                  {rows.map((row) => {
                    const cat = { label: visibleRolesLabel(roles, row.visible_roles), color: (CATEGORY_META[row.category || "ocio"] || CATEGORY_META.ocio).color };
                    return (
                      <li
                        key={row.id}
                        data-testid={`task-row-${row.id}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${
                          row._consecutive ? "border-dashed border-[var(--border)] ml-6" : "border-[var(--border)]"
                        }`}
                      >
                        <span
                          className="w-2 h-10 rounded-full shrink-0"
                          style={{ background: row.family_color || "#94a3b8" }}
                          title={row.family_name || ""}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-semibold text-sm">{row.user_name}</span>
                            <span className="text-xs text-[var(--text-secondary)]">·</span>
                            <span className="text-sm">{row.reason}</span>
                            {row.custom_reason && (
                              <span className="text-xs text-[var(--text-secondary)]">— {row.custom_reason}</span>
                            )}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-display font-bold ${cat.color}`}>
                              {cat.label}
                            </span>
                            {row.is_open && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider font-display font-bold bg-amber-100 text-amber-700 border-amber-300">
                                En curso
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--text-secondary)] mt-1 flex items-center gap-2 flex-wrap">
                            <Clock size={12} weight="duotone" />
                            <span>{fmtTime(row.start_time)} → {row.end_time ? fmtTime(row.end_time) : "…"}</span>
                            <span className="font-mono">· {fmtDur(row.duration_hours)}</span>
                            {row.end_comment && (
                              <>
                                <ChatCircleText size={12} weight="duotone" />
                                <span className="italic">“{row.end_comment}”</span>
                              </>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          /* MATRIX VIEW */
          <div className="overflow-x-auto -mx-6 px-6" data-testid="tasks-matrix">
            <table className="min-w-full border border-[var(--border)] bg-white rounded-xl text-sm">
              <thead>
                <tr className="bg-[var(--bg)]">
                  <th className="text-left px-3 py-2 border-b border-r border-[var(--border)]">
                    <SortHeader sortKey="reason" currentKey={matrixSort.sortKey} currentDir={matrixSort.sortDir} onClick={matrixSort.toggleSort}>Motivo</SortHeader>
                  </th>
                  {matrix.dayKeys.map((d) => (
                    <th key={d} className="text-left px-3 py-2 border-b border-r border-[var(--border)] whitespace-nowrap">
                      <SortHeader sortKey={d} currentKey={matrixSort.sortKey} currentDir={matrixSort.sortDir} onClick={matrixSort.toggleSort}>{fmtDate(d + "T00:00:00")}</SortHeader>
                    </th>
                  ))}
                  <th className="text-right px-3 py-2 border-b border-[var(--border)] whitespace-nowrap">
                    <SortHeader align="right" sortKey="total" currentKey={matrixSort.sortKey} currentDir={matrixSort.sortDir} onClick={matrixSort.toggleSort}>Total</SortHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {matrixSort.sortedRows.map(({ reason }) => {
                  const dayMap = matrix.cells.get(reason) || new Map();
                  let rowTotal = 0;
                  return (
                    <tr key={reason} data-testid={`matrix-row-${reason}`}>
                      <td className="px-3 py-2 border-b border-r border-[var(--border)] font-display font-semibold align-top">
                        {reason}
                      </td>
                      {matrix.dayKeys.map((d) => {
                        const userMap = dayMap.get(d);
                        if (!userMap || userMap.size === 0) {
                          return (
                            <td key={d} className="px-3 py-2 border-b border-r border-[var(--border)] align-top text-[var(--text-secondary)]">
                              —
                            </td>
                          );
                        }
                        const items = Array.from(userMap.entries())
                          .sort((a, b) => a[0].localeCompare(b[0], "es", { sensitivity: "base" }));
                        const cellTotal = items.reduce((s, [, v]) => s + v.hours, 0);
                        rowTotal += cellTotal;
                        return (
                          <td key={d} className="px-3 py-2 border-b border-r border-[var(--border)] align-top">
                            <div className="font-mono font-bold text-xs mb-1">{fmtDur(cellTotal)}</div>
                            <ul className="space-y-0.5">
                              {items.map(([name, v]) => (
                                <li key={name} className="flex items-center gap-1.5 text-xs">
                                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: v.family_color }} />
                                  <span className="truncate">{name}</span>
                                  <span className="ml-auto font-mono text-[var(--text-secondary)]">{fmtDur(v.hours)}</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 border-b border-[var(--border)] align-top text-right font-mono font-bold">
                        {fmtDur(rowTotal)}
                      </td>
                    </tr>
                  );
                })}
                {/* Totals row — day-column sums for cross-day comparison */}
                <tr className="bg-[var(--bg)]" data-testid="matrix-totals-row">
                  <td className="px-3 py-2 border-r border-[var(--border)] font-display font-bold uppercase label-cap">
                    Total por día
                  </td>
                  {matrix.dayKeys.map((d) => (
                    <td
                      key={d}
                      data-testid={`matrix-total-col-${d}`}
                      className="px-3 py-2 border-r border-[var(--border)] font-mono font-bold text-right"
                    >
                      {fmtDur(matrix.colTotals[d] || 0)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-mono font-bold" data-testid="matrix-grand-total">
                    {fmtDur(matrix.grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
